import { useCallback, useEffect, useState } from 'react';
import Page from '../../../../components/page/Page';
import Header from '../../../../components/administration/masters/types/Header';
import { Box, Button, IconButton, Paper, Tab, Tabs } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useMeCore } from '../../../../../core/modules/me';
import { useGlobalCore } from '../../../../../core/modules/global';
import CreateDialog from '../../../../components/administration/masters/types/CreateDialog';
import UpdateDialog from '../../../../components/administration/masters/types/UpdateDialog';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import EditIcon from '@mui/icons-material/Edit';
import { CREATE_TYPE_WITH_LIMIT_DIALOG, UPDATE_TYPE_DIALOG, UPDATE_TYPE_WITH_LIMIT_DIALOG, CREATE_TYPE_WITH_FIELDS_DIALOG, UPDATE_TYPE_WITH_FIELDS_DIALOG } from '../../../../../core/constants/dialog';
import { Type } from '../../../../../core/interfaces/type.interface';
import { CREATE_TYPE_DIALOG } from '../../../../../core/constants/dialog';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CreateDialogWithLimit from '../../../../components/administration/masters/types/CreateDialogWithLimit';
import UpdateDialogWithLimit from '../../../../components/administration/masters/types/UpdateDialogWithLimit';
import CreateDialogWithFields from '../../../../components/administration/masters/types/CreateDialogWithFields';
import UpdateDialogWithFields from '../../../../components/administration/masters/types/UpdateDialogWithFields';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Types = () => {
  const { user } = useMeCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const {
    productTypes,
    warehouseTypes,
    locationTypes,
    containerTypes,
    channelTypes,
    acquisitionTypes,
    receptionTypes,
    containerRecipientTypes,
    setType,
    getTypes,
    clearError,
  } = useMasterCore();
  const { goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'))
  const [tab, setTab] = useState(0);
  const [tType, setTType] = useState<tType>('product');

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.writeMaster
      )
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId) {
      getTypes(companyId, 'product');
      getTypes(companyId, 'warehouse');
      getTypes(companyId, 'location');
      getTypes(companyId, 'container');
      getTypes(companyId, 'channel');
      getTypes(companyId, 'acquisition');
      getTypes(companyId, 'reception');
      getTypes(companyId, 'containerRecipient');
    }
  }, [companyId, getTypes]);

  const selectType = useCallback((type: Type) => {
    clearError();
    setType(type);
    switch (tab) {
      case 0:
        setTType('product');
        openDialog(UPDATE_TYPE_WITH_FIELDS_DIALOG);
        break;
      case 1:
        setTType('warehouse');
        openDialog(UPDATE_TYPE_DIALOG);
        break;
      case 2:
        setTType('location');
        openDialog(UPDATE_TYPE_DIALOG);
        break;
      case 3:
        setTType('container');
        openDialog(UPDATE_TYPE_WITH_LIMIT_DIALOG);
        break;
      case 4:
        setTType('channel');
        openDialog(UPDATE_TYPE_DIALOG);
        break;
      case 5:
        setTType('acquisition');
        openDialog(UPDATE_TYPE_DIALOG);
        break;
      case 6:
        setTType('reception');
        openDialog(UPDATE_TYPE_DIALOG);
        break;
      case 7:
        setTType('containerRecipient');
        openDialog(UPDATE_TYPE_DIALOG);
        break;
      default:
        setTType('reception');
        openDialog(UPDATE_TYPE_DIALOG);
    }
  }, [clearError, openDialog, setType, tab]);

  const columns1: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      sortable: true,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 1,
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'text-end',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton aria-label="edit" onClick={() => selectType(params.row)}>
          <EditIcon />
        </IconButton>
      )
    },
  ];
  const columns2: GridColDef[] = [
    {
      field: 'description',
      headerName: 'Sigla',
      flex: 1,
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'text-end',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton aria-label="edit" onClick={() => selectType(params.row)}>
          <EditIcon />
        </IconButton>
      )
    },
  ];

  const openCreateDialog = useCallback(() => {
    clearError();
    switch (tab) {
      case 0:
        setTType('product');
        openDialog(CREATE_TYPE_WITH_FIELDS_DIALOG);
        break;
      case 1:
        setTType('warehouse');
        openDialog(CREATE_TYPE_DIALOG);
        break;
      case 2:
        setTType('location');
        openDialog(CREATE_TYPE_DIALOG);
        break;
      case 3:
        setTType('container');
        openDialog(CREATE_TYPE_WITH_LIMIT_DIALOG);
        break;
      case 4:
        setTType('channel');
        openDialog(CREATE_TYPE_DIALOG);
        break;
      case 5:
        setTType('acquisition');
        openDialog(CREATE_TYPE_DIALOG);
        break;
      case 6:
        setTType('reception');
        openDialog(CREATE_TYPE_DIALOG);
        break;
      case 7:
        setTType('containerRecipient');
        openDialog(CREATE_TYPE_DIALOG);
        break;
      default:
        setTType('product');
        openDialog(CREATE_TYPE_DIALOG);
    }
  }, [clearError, openDialog, tab]);

  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <Tabs
                    value={tab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Tipo de producto" {...a11yProps(0)} />
                    <Tab label="Tipo de bodega" {...a11yProps(1)} />
                    <Tab label="Tipo de ubicación" {...a11yProps(2)} />
                    <Tab label="Tipo de contenedor" {...a11yProps(3)} />
                    <Tab label="Tipo de canal" {...a11yProps(4)} />
                    <Tab label="Tipo de adquisición" {...a11yProps(5)} />
                    <Tab label="Tipo de recepción" {...a11yProps(6)} />
                    <Tab label="Tipo de recipiente" {...a11yProps(7)} />
                  </Tabs>
                  <Button
                    variant="contained"
                    color="secondary"
                    className="mr-8 mb-8"
                    disabled={!companyId}
                    onClick={openCreateDialog}
                  >
                    <AddCircleOutlineOutlinedIcon />
                    <span className="mx-8">Crear</span>
                  </Button>
                </Box>
                <CustomTabPanel value={tab} index={0}>
                  <DataGrid
                    rows={productTypes}
                    columns={columns1}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={1}>
                  <DataGrid
                    rows={warehouseTypes}
                    columns={columns1}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={2}>
                  <DataGrid
                    rows={locationTypes}
                    columns={columns1}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={3}>
                  <DataGrid
                    rows={containerTypes}
                    columns={columns2}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={4}>
                  <DataGrid
                    rows={channelTypes}
                    columns={columns1}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={5}>
                  <DataGrid
                    rows={acquisitionTypes}
                    columns={columns1}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={6}>
                  <DataGrid
                    rows={receptionTypes}
                    columns={columns1}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={7}>
                  <DataGrid
                    rows={containerRecipientTypes}
                    columns={columns1}
                    sx={{ border: 0 }}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    pagination={undefined}
                  />
                </CustomTabPanel>
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog type={tType} />
      <CreateDialogWithLimit type={tType} />
      <CreateDialogWithFields type={tType} />
      <UpdateDialog type={tType} />
      <UpdateDialogWithLimit type={tType} />
      <UpdateDialogWithFields type={tType} />
    </>
  );
};

export default Types;
